/* eslint-disable @typescript-eslint/no-explicit-any */
import { Helmet } from 'react-helmet';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Statistic, Tooltip, Typography } from 'antd';

import { PageProps } from '@/types/PageProps';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import GroupingForm from '@/components/Registry/BillToReceive/AccountReceivableGroupingFilter';
import { useAccountReceivableListForGrouping } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import {
  AccountReceivableDebtor,
  ListAccountReceivableForGroupingRequest,
  ListAccountReceivableForGroupingResponse,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { useCallback, useState } from 'react';
import { COLUMNS_WIDTH } from '@/utils/table';
import AccountReceivableGroupingInstallmentModal from '@/components/Registry/BillToReceive/AccountReceivableGenerateInstallmentModal';
import { useDebounce, useDisclosure } from '@/hooks';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { quantityFormatter } from '@/utils/number';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import formatterPrice from '@/utils/number/formatterPrice';

function GroupingsBillToReceivePage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] =
    useState<ListAccountReceivableForGroupingRequest>({
      debtor: AccountReceivableDebtor.Costumer,
      ...convertParams<ListAccountReceivableForGroupingRequest>({
        companyId: 'number',
        debtor: 'string',
        insurerId: 'number',
      }),
    });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    useAccountReceivableListForGrouping(queryDebounce);
  const dataList = flatInfiniteData(data);

  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [modalOpened, modalAction] = useDisclosure();

  const columns: ColumnsType<ListAccountReceivableForGroupingResponse> = [
    {
      width: COLUMNS_WIDTH.default,
      dataIndex: 'code',
      title: intl.formatMessage({
        id: 'groupings.list.header.code',
      }),
    },
    {
      width: COLUMNS_WIDTH.default,
      dataIndex: 'description',
      title: intl.formatMessage({
        id: 'groupings.list.header.description',
      }),
    },
    {
      width: COLUMNS_WIDTH.default,
      dataIndex: 'installmentNumber',
      title: intl.formatMessage({
        id: 'groupings.list.header.installments',
      }),
    },
    {
      width: COLUMNS_WIDTH.default,
      dataIndex: 'totalValue',
      title: intl.formatMessage({
        id: 'groupings.list.header.amount',
      }),
      render: (value: number) => formatterPrice(value),
      align: 'right',
    },
  ];

  const getTotalValue = useCallback(
    () =>
      dataList.reduce(
        (acc, item) =>
          selectedRowKeys.includes(item.accountId)
            ? item.totalValue + acc
            : acc,
        0,
      ),
    [selectedRowKeys],
  );

  const handleCancelAfterSubmit = () => {
    modalAction.close();
    setSelectedRowKeys([]);
  };

  return (
    <DefaultRouteTabs
      route={route.parent}
      persistSearchParams={false}
      permissions={route.permissions}
      extra={
        <SpaceForButton>
          <Tooltip
            title={
              selectedRowKeys.length < 2
                ? 'Selecione 2 ou mais contas'
                : undefined
            }
          >
            <Button
              block
              type="primary"
              disabled={
                selectedRowKeys.length < 2 ||
                !hasPermissions([
                  Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_GROUP,
                ])
              }
              onClick={modalAction.open}
            >
              <FormattedMessage id="groupings.generate.clusters" />
            </Button>
          </Tooltip>
        </SpaceForButton>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <GroupingForm
        filter={queryParams}
        onFilter={setQueryParams}
        loading={isLoading}
      />

      <Typography.Title level={3}>Contas em aberto</Typography.Title>

      <InfiniteTable<any>
        rowSelection={{
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
          selectedRowKeys,
        }}
        columns={columns}
        dataSource={dataList}
        loading={isFetching}
        rowKey={(row) => row.accountId}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        style={{ marginTop: 24 }}
        footer={() => (
          <Statistic
            style={{ textAlign: 'end' }}
            valueStyle={{ color: 'rgb(160, 206, 51)' }}
            title="Valor de agrupamento"
            value={`R$ ${quantityFormatter(getTotalValue())}`}
          />
        )}
      />

      <AccountReceivableGroupingInstallmentModal
        open={modalOpened}
        onCancel={modalAction.close}
        data={{ installmentIds: selectedRowKeys, totalValue: getTotalValue() }}
        cancelAfterSubmit={handleCancelAfterSubmit}
      />
    </DefaultRouteTabs>
  );
}

export default GroupingsBillToReceivePage;
